import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { 
  FaPlay, 
  FaPause, 
  FaVolumeUp, 
  FaExpand, 
  FaDownload, 
  FaFileAlt,
  FaQuestionCircle,
  FaCheckCircle,
  FaClock,
  FaBook,
  FaVideo,
  FaList,
  FaStar,
  FaUser,
  FaCalendarAlt,
  FaTrophy,
  FaArrowLeft,
  FaArrowRight,
  FaBookmark,
  FaShare,
  FaComment
} from 'react-icons/fa';
import { getCourseById, clearCourse } from '../../features/courses/courseSlice';
import { getLessonsByCourse, clearLesson } from '../../features/lessons/lessonSlice';
import { getCourseProgress } from '../../features/progress/progressSlice';

const CourseDetail = () => {
  const { courseId } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { courses } = useSelector((state) => state.courses);
  const { progress } = useSelector((state) => state.progress);
  
  const [currentLesson, setCurrentLesson] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Mock course data
  const course = courses.find(c => c._id === courseId) || {
    _id: courseId,
    title: 'Advanced React Development',
    description: 'Master React with hooks, context, and advanced patterns',
    instructor: 'John Doe',
    category: 'Web Development',
    level: 'Advanced',
    duration: 15,
    studentsEnrolled: 1250,
    averageRating: 4.8,
    thumbnail: '/internet1.webp',
    lessons: [
      {
        id: 1,
        title: 'Introduction to React Hooks',
        type: 'video',
        duration: 45,
        isCompleted: false,
        videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4'
      },
      {
        id: 2,
        title: 'useState Hook Deep Dive',
        type: 'video',
        duration: 60,
        isCompleted: false,
        videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4'
      },
      {
        id: 3,
        title: 'useEffect Hook Patterns',
        type: 'video',
        duration: 55,
        isCompleted: false,
        videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4'
      },
      {
        id: 4,
        title: 'React Hooks Quiz',
        type: 'quiz',
        duration: 30,
        isCompleted: false,
        questions: 10
      },
      {
        id: 5,
        title: 'Custom Hooks Development',
        type: 'video',
        duration: 70,
        isCompleted: false,
        videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4'
      },
      {
        id: 6,
        title: 'Final Project Assignment',
        type: 'assignment',
        duration: 120,
        isCompleted: false,
        dueDate: '2023-12-25'
      }
    ]
  };

  useEffect(() => {
    dispatch(getCourseById(courseId));
    dispatch(getLessonsByCourse(courseId));
    dispatch(getCourseProgress(courseId));
    
    if (course.lessons && course.lessons.length > 0) {
      setCurrentLesson(course.lessons[0]);
    }
    
    return () => {
      dispatch(clearCourse());
      dispatch(clearLesson());
    };
  }, [dispatch, courseId]);

  const handleVideoProgress = (e) => {
    const video = e.target;
    setCurrentTime(video.currentTime);
    setDuration(video.duration);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value;
    setVolume(newVolume);
    // Update video volume
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
    // Toggle video play/pause
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':
        return <FaVideo className="text-blue-500" />;
      case 'quiz':
        return <FaQuestionCircle className="text-green-500" />;
      case 'assignment':
        return <FaFileAlt className="text-orange-500" />;
      default:
        return <FaBook className="text-gray-500" />;
    }
  };

  const getLessonStatus = (lesson) => {
    if (lesson.isCompleted) {
      return <FaCheckCircle className="text-green-500" />;
    }
    return <FaClock className="text-gray-400" />;
  };

  const calculateProgress = () => {
    if (!course.lessons) return 0;
    const completedLessons = course.lessons.filter(lesson => lesson.isCompleted).length;
    return Math.round((completedLessons / course.lessons.length) * 100);
  };

  const progressPercentage = calculateProgress();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Calculate course duration
  const calculateDuration = (lessons) => {
    if (!lessons || lessons.length === 0) return '0 hours';
    
    const totalMinutes = lessons.reduce((total, lesson) => {
      return total + (lesson.duration || 0);
    }, 0);
    
    if (totalMinutes < 60) return `${totalMinutes} minutes`;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
  };
  
  // Check if a lesson is completed
  const isLessonCompleted = (lessonId) => {
    if (!progress || !progress.completedLessons) return false;
    return progress.completedLessons.includes(lessonId);
  };
  
  // Check if a lesson is accessible
  const isLessonAccessible = (index) => {
    // First lesson is always accessible
    if (index === 0) return true;
    
    // If previous lesson is completed, this lesson is accessible
    const previousLesson = course.lessons[index - 1];
    if (previousLesson && isLessonCompleted(previousLesson._id)) return true;
    
    return false;
  };
  
  // Loading state
  if (!course) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">Course Not Found</h2>
        <p className="text-gray-600 mb-6">The course you're looking for doesn't exist or you don't have access to it.</p>
        <Link to="/dashboard/courses" className="btn-primary">
          Back to My Courses
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Link 
                to="/dashboard/courses"
                className="text-gray-600 hover:text-gray-900"
              >
                <FaArrowLeft />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{course.title}</h1>
                <p className="text-sm text-gray-500">by {course.instructor}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-600 hover:text-gray-900">
                <FaBookmark />
              </button>
              <button className="p-2 text-gray-600 hover:text-gray-900">
                <FaShare />
              </button>
              <div className="flex items-center space-x-2">
                <FaStar className="text-yellow-400" />
                <span className="text-sm font-medium">{course.averageRating}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            {currentLesson && currentLesson.type === 'video' && (
              <div className="bg-white container-rounded-lg shadow-sm overflow-hidden">
                <div className="relative bg-black aspect-video">
                  <video
                    className="w-full h-full"
                    src={currentLesson.videoUrl}
                    onTimeUpdate={handleVideoProgress}
                    onLoadedMetadata={handleVideoProgress}
                  />
                  
                  {/* Video Controls Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={togglePlayPause}
                        className="text-white hover:text-gray-300"
                      >
                        {isPlaying ? <FaPause /> : <FaPlay />}
                      </button>
                      
                      <div className="flex-1">
                        <div className="w-full bg-gray-600 rounded-full h-1">
                          <div 
                            className="bg-white h-1 rounded-full" 
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <span className="text-white text-sm">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                      
                      <div className="flex items-center space-x-2">
                        <FaVolumeUp className="text-white" />
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={handleVolumeChange}
                          className="w-20"
                        />
                      </div>
                      
                      <button className="text-white hover:text-gray-300">
                        <FaExpand />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Lesson Info */}
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-2">{currentLesson.title}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {currentLesson.duration} minutes
                    </span>
                    <span className="flex items-center">
                      <FaVideo className="mr-1" />
                      Video Lesson
                    </span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-4">
                    <button className="bg-primary text-white px-4 py-2 container-rounded-lg hover:bg-primary-dark transition-colors">
                      Mark as Complete
                    </button>
                    <button 
                      onClick={() => setShowNotes(!showNotes)}
                      className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                      <FaFileAlt className="mr-2" />
                      Take Notes
                    </button>
                    <button className="flex items-center text-gray-600 hover:text-gray-900">
                      <FaDownload className="mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quiz/Assignment Content */}
            {currentLesson && currentLesson.type !== 'video' && (
              <div className="bg-white container-rounded-lg shadow-sm p-6">
                <div className="flex items-center space-x-3 mb-4">
                  {getLessonIcon(currentLesson.type)}
                  <h2 className="text-xl font-bold">{currentLesson.title}</h2>
                </div>
                
                {currentLesson.type === 'quiz' && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      This quiz contains {currentLesson.questions} questions and should take approximately {currentLesson.duration} minutes to complete.
                    </p>
                    <button className="bg-primary text-white px-6 py-3 container-rounded-lg hover:bg-primary-dark transition-colors">
                      Start Quiz
                    </button>
                  </div>
                )}
                
                {currentLesson.type === 'assignment' && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      This assignment is due on {new Date(currentLesson.dueDate).toLocaleDateString()}.
                    </p>
                    <button className="bg-primary text-white px-6 py-3 container-rounded-lg hover:bg-primary-dark transition-colors">
                      View Assignment
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Notes Section */}
            {showNotes && (
              <div className="bg-white container-rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold mb-4">My Notes</h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Take notes about this lesson..."
                  className="w-full h-32 p-3 border border-gray-300 container-rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <div className="flex justify-end mt-4">
                  <button className="bg-primary text-white px-4 py-2 container-rounded-lg hover:bg-primary-dark transition-colors">
                    Save Notes
                  </button>
                </div>
              </div>
            )}

            {/* Course Tabs */}
            <div className="bg-white container-rounded-lg shadow-sm">
              <div className="border-b">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'overview', label: 'Overview', icon: <FaBook /> },
                    { id: 'content', label: 'Content', icon: <FaList /> },
                    { id: 'reviews', label: 'Reviews', icon: <FaStar /> },
                    { id: 'discussions', label: 'Discussions', icon: <FaComment /> }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
              
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-lg font-bold mb-4">Course Overview</h3>
                    <p className="text-gray-600 mb-4">{course.description}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <FaUser className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">Instructor: {course.instructor}</span>
                      </div>
                      <div className="flex items-center">
                        <FaCalendarAlt className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">Duration: {course.duration} hours</span>
                      </div>
                      <div className="flex items-center">
                        <FaBook className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">Category: {course.category}</span>
                      </div>
                      <div className="flex items-center">
                        <FaTrophy className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">Level: {course.level}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 'content' && (
                  <div>
                    <h3 className="text-lg font-bold mb-4">Course Content</h3>
                    <div className="space-y-2">
                      {course.lessons?.map((lesson, index) => (
                        <div 
                          key={lesson.id}
                          className={`flex items-center justify-between p-3 container-rounded-lg border cursor-pointer transition-colors ${
                            currentLesson?.id === lesson.id 
                              ? 'border-primary bg-primary/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setCurrentLesson(lesson)}
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-500 w-8">{index + 1}</span>
                            {getLessonIcon(lesson.type)}
                            <span className="font-medium">{lesson.title}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">{lesson.duration} min</span>
                            {getLessonStatus(lesson)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {activeTab === 'reviews' && (
                  <div>
                    <h3 className="text-lg font-bold mb-4">Student Reviews</h3>
                    <p className="text-gray-500">Reviews will appear here...</p>
                  </div>
                )}
                
                {activeTab === 'discussions' && (
                  <div>
                    <h3 className="text-lg font-bold mb-4">Course Discussions</h3>
                    <p className="text-gray-500">Discussions will appear here...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <div className="bg-white container-rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold mb-4">Your Progress</h3>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Course Progress</span>
                  <span>{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p>{course.lessons?.filter(l => l.isCompleted).length || 0} of {course.lessons?.length || 0} lessons completed</p>
              </div>
            </div>

            {/* Course Stats */}
            <div className="bg-white container-rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold mb-4">Course Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Students Enrolled</span>
                  <span className="text-sm font-medium">{course.studentsEnrolled}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Average Rating</span>
                  <span className="text-sm font-medium">{course.averageRating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Duration</span>
                  <span className="text-sm font-medium">{course.duration} hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm font-medium">Dec 2023</span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="bg-white container-rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold mb-4">Navigation</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 container-rounded-lg border border-gray-200 hover:border-primary transition-colors">
                  <span className="text-sm">Previous Lesson</span>
                  <FaArrowLeft />
                </button>
                <button className="w-full flex items-center justify-between p-3 container-rounded-lg border border-gray-200 hover:border-primary transition-colors">
                  <span className="text-sm">Next Lesson</span>
                  <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;